import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CreditCard, Star, Zap, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CreditPurchaseDialog = ({ isOpen, onClose, onPurchaseSuccess }) => {
  const { refreshCredits } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await api.credits.getPackages();
      setPackages(response.data.filter(pkg => pkg.active !== false));
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    setPurchasing(pkg.id);
    try {
      await api.credits.purchase({ package_id: pkg.id, payment_method: 'demo' });
      toast.success(`Successfully purchased ${pkg.credits + (pkg.bonus_credits || 0)} credits!`);
      refreshCredits();
      if (onPurchaseSuccess) {
        onPurchaseSuccess();
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const getPackageIcon = (pkg) => {
    if (pkg.name?.toLowerCase().includes('starter')) return Zap;
    if (pkg.name?.toLowerCase().includes('popular') || pkg.popular) return Star;
    if (pkg.name?.toLowerCase().includes('premium') || pkg.name?.toLowerCase().includes('mega')) return Crown;
    return CreditCard;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Credits kaufen</DialogTitle>
          <DialogDescription>
            Wähle ein Paket um weiter zu spielen
          </DialogDescription>
        </DialogHeader>

        {/* Test-Modus-Banner: Showcase-Beta laeuft ohne echte Zahlung.
            Käufe gehen durch ein Demo-Backend, kein Stripe-Live. */}
        <div className="mt-2 mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-500/10">
          <p className="text-sm font-medium">🧪 Test-Modus</p>
          <p className="text-xs mt-0.5">
            PredFuture läuft aktuell als kostenloses Showcase. Käufe werden im Test-Backend
            verbucht – keine echten Beträge fließen, keine Zahlungsdaten werden erhoben.
            Tagesbonus und Empfehlungs-Programm laufen normal.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 dark:text-emerald-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {packages.map((pkg) => {
              const Icon = getPackageIcon(pkg);
              return (
                <Card 
                  key={pkg.id}
                  className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                    pkg.popular ? 'border-2 border-emerald-500 shadow-emerald-100' : ''
                  }`}
                  onClick={() => handlePurchase(pkg)}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Most Popular
                    </div>
                  )}
                  <CardContent className="pt-6 pb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${pkg.popular ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                        <Icon className={`h-6 w-6 ${pkg.popular ? 'text-emerald-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{pkg.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {pkg.credits.toLocaleString()} credits
                          {pkg.bonus_credits > 0 && (
                            <span className="text-emerald-600 ml-1 dark:text-emerald-400">+{pkg.bonus_credits} bonus</span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-2xl font-bold">${pkg.price?.toFixed(2)}</span>
                      <Button 
                        className={pkg.popular ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                        disabled={purchasing === pkg.id}
                      >
                        {purchasing === pkg.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        Buy Now
                      </Button>
                    </div>
                    
                    {pkg.price_per_credit && (
                      <p className="text-xs text-gray-400 mt-2 dark:text-gray-500">
                        ${pkg.price_per_credit.toFixed(4)} per credit
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-4 dark:text-gray-400">
          * This is a demo using virtual credits. No real payment is processed.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CreditPurchaseDialog;
